import React, { useEffect, useCallback, useMemo } from "react";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignLeft,
  faAlignCenter,
  faAlignRight
} from "@fortawesome/pro-regular-svg-icons";
import Hypher from "hypher";
import hyphenationLatin from "../../js/la-hypher";
import hyphenationEn from "hyphenation.en-us";

import Panel from "../Panel";
import PanelSection from "../PanelSection";
import ColorPicker from "../ColorPicker";
import { defaultColor, colors } from "../../utils/colors";
import NumericFormControl from "../NumericFormControl";
import { defaultTitleAlignment } from "../App";
import TextSelection from "../../js/TextSelection";
import ChantDocument from "../../js/ChantDocument";
import * as exsurge from "react-exsurge";
import MouseEventUtils from "../../js/MouseEventUtils";
import FontPane from "./FontPane";
// import EnglishSyl from "../../js/EnglishSyl"; // juiciobrennan.com/syllables/

const hypherLatin = new Hypher(hyphenationLatin);
const hypherEnglish = new Hypher(hyphenationEn);

const noop = () => {};

async function syllabifyText(text, language) {
  var hypher = language === "en" ? hypherEnglish : hypherLatin;
  var hyphenated = await Promise.resolve(hypher.hyphenateText(text, 1));
  return hyphenated.replace(/(-|\S(?=\s|$))|\u00AD/g, "$1()");
}

function TextPanel(props) {
  const doc = props.document;
  const selectedTextTypes = [];
  var isTitleSelected = false;
  var isLyricSelected = false;
  var isSyllabified = null;
  props.selection.forEach(({ source }) => {
    const { textType } = source;
    if (!selectedTextTypes.includes(textType)) {
      selectedTextTypes.push(textType);
      if (!isTitleSelected && /title$/.test(textType.key))
        isTitleSelected = true;
      isLyricSelected = textType.key === "lyric";
      if (isLyricSelected) {
        let notation = source.notation,
          notations = notation.mapping.notations;
        if (notations[0] instanceof exsurge.Clef)
          notations = notations.slice(1);
        // if (notations.every(notation => notation instanceof exsurge.TextOnly)) {
        //   isSyllabified = source.lyricType !== exsurge.LyricType.SingleSyllable;
        // }
      }
    }
  });
  const selectedTextType = ChantDocument.getTextType(
    doc,
    selectedTextTypes[0].key
  );

  const handleChange = (newValue, key) => {
      let docChange = selectedTextTypes.reduce(
        (result, textType) => ({ ...result, [textType.key + key]: newValue }),
        {}
      );
      props.onChange(docChange);
    },
    handleFontChange = newFont => handleChange(newFont, "Font"),
    handleColorChange = newColor => handleChange(newColor, "Color"),
    handleAlignmentChange = newAlignment =>
      handleChange(newAlignment, "Alignment");
  const textElem = props.selection[0].source,
    key = textElem.textType.key,
    fontKey = key + "Font",
    colorKey = key + "Color",
    alignmentKey = key + "Alignment",
    activeAlignment = doc[alignmentKey] || defaultTitleAlignment,
    spans = textElem.spans;
  const { start: selStart = 0, end: selEnd = Infinity } = props.selection[0];

  const {
    selectedText,
    activeTags,
    start,
    end,
  } = useMemo(() => {
    let start = selStart;
    let end = selEnd;
    //    console.info({start, end})
    if (start > 0 && start === end) {
      // when the selection is just a cursor, we can operate on the previous character:
      start--;
    }
    const activeTags = {};
    let selectedText = "";
    let firstSpan = true;
    let notYetInSelectionRange = true;
    for (let i = 0, index = 0; i < spans.length; ++i) {
      let span = spans[i];
      let spanStartIndex = index;
      index += span.text.length;
      notYetInSelectionRange = notYetInSelectionRange && index <= start;
      if (notYetInSelectionRange) continue;
      if (firstSpan) {
        span.activeTags.forEach(tag => (activeTags[tag] = true));
        firstSpan = false;
        selectedText += span.text.slice(start - spanStartIndex);
        while (
          start - spanStartIndex >= 1 &&
          TextSelection.isCombinationCode(selectedText[0])
        ) {
          --start;
          let index = start - spanStartIndex;
          selectedText = span.text.slice(index, index + 1) + selectedText;
        }
      } else {
        selectedText += span.text;
        [...span.activeTags, ...Object.keys(activeTags)].forEach(
          tag =>
            (activeTags[tag] = activeTags[tag] && span.activeTags.includes(tag))
        );
      }
      if (index >= end) break;
    }
    let possibleCombiningMarks = TextSelection.getCombinationCodesAtBeginning(
      selectedText.slice(end - start)
    );
    end += possibleCombiningMarks.length;
    selectedText = selectedText.slice(0, end - start);
    return {
      selectedText,
      activeTags,
      start,
      end,
    }
  }, [selEnd, selStart, spans]);

  let selectedChar = TextSelection.getSingleChar(selectedText);
  const selectedCharWithoutMarks = TextSelection.removeDiacriticalMarks(
    selectedChar
  );

  const TextMarkButtons = TextSelection.Marks.map(mark => ({
    ...mark,
    reSelected: new RegExp(mark.reNone.source + mark.sym, mark.reNone.flags)
  }))
    .filter(({ reNone }) => {
      return reNone.test(selectedCharWithoutMarks || "");
    })
    .map(({ key, sym, left, reSelected, reWith }) => {
      let active = reSelected.test(selectedChar) || reWith.test(selectedChar);
      return (
        <Button
          key={key}
          variant="circle"
          active={active}
          value={key}
          onClick={() =>
            props.onUpdateText(
              props.selection.map(sel => sel.source),
              props.selection.map(({ source }) =>
                TextSelection.applyTextMark(
                  source.sourceGabc,
                  sym,
                  !!active,
                  start,
                  end
                )
              )
            )
          }
        >
          <span className="inactive">a</span>
          <span style={{ position: "relative", left: left || 0 }}>{sym}</span>
        </Button>
      );
    });

  const onUpdateText = props.onUpdateText || noop;
  const syllabifySelection = useCallback(() => {
    let lyrics = props.selection
      .map(({ source }) => source)
      .filter(source => source instanceof exsurge.Lyric);
    const notation = lyrics[0].notation,
      lyricI = notation.lyrics.indexOf(lyrics[0]),
      notations = notation.score.notations;
    for (let i = notations.indexOf(notation) - 1; i >= 0; i--) {
      let notationI = notations[i],
        lyric = notationI.lyrics[lyricI];
      if (
        notationI instanceof exsurge.TextOnly &&
        lyric &&
        lyric.lyricType === exsurge.LyricType.SingleSyllable
      ) {
        lyrics.unshift(lyric);
      } else break;
    }
    for (
      let i =
        notations.indexOf(lyrics.slice(-1)[0].notation) + 1 || notations.length;
      i < notations.length;
      i++
    ) {
      let notationI = notations[i],
        lyric = notationI.lyrics[lyricI];
      if (
        notationI instanceof exsurge.TextOnly &&
        lyric &&
        lyric.lyricType === exsurge.LyricType.SingleSyllable
      ) {
        lyrics.push(lyric);
      } else break;
    }
    return Promise.all(
      lyrics.map(source => {
        let text = source.sourceGabc;
        if (source.dropCap && source.dropCap.sourceGabc.length === 1) {
          text = source.dropCap.sourceGabc + text;
        }
        return syllabifyText(text, (doc.alignment || "la").slice(0, 2)).then(
          syllabified => {
            if (source.dropCap && source.dropCap.sourceGabc.length === 1) {
              syllabified = syllabified.slice(1);
            }
            return { syllabified, source };
          }
        );
      })
    ).then(result => {
      onUpdateText(
        result.map(sel => sel.source),
        result.map(({ syllabified }) => syllabified)
      );
    });
  }, [onUpdateText, props.selection, doc]);
  const unsyllabifySelection = useCallback(() => {
    const wordNotations = props.selection[0].source.notation.mapping.notations;
    // TODO: implement unsyllabification
  }, [onUpdateText, props.selection]);
  const applyTag = useCallback(
      tagName =>
        onUpdateText(
          props.selection.map(sel => sel.source),
          props.selection.map(({ source, start = 0, end = Infinity }) => {
            if (start === end) {
              start = 0;
              end = Infinity;
            }
            return TextSelection.applyFormatTag(
              source.sourceGabc,
              tagName,
              !!activeTags[tagName],
              start,
              end
            );
          })
        ),
      [activeTags, onUpdateText, props.selection]
    ),
    textOptions = [
      { tagName: "b", icon: "B", iconClass: "font-weight-bold" },
      { tagName: "i", icon: "I", iconClass: "font-italic" },
      { tagName: "u", icon: "U", iconClass: "text-underline" },
      { tagName: "sc", icon: "SC", iconClass: "text-small-caps" }
    ],
    textOptionsByTagName = textOptions.reduce(
      (result, textOption) => ({ ...result, [textOption.tagName]: textOption }),
      {}
    );

  const handleKeyDown = useCallback(
    e => {
      let hasModKey = MouseEventUtils.hasModKey(e);
      if (hasModKey && !e.shiftKey) {
        let textOption = textOptionsByTagName[e.key];
        if (textOption) {
          applyTag(textOption.tagName);
          e.preventDefault();
        }
      }
      if (e.target.tagName === 'INPUT') {
        // allow text entry into input fields
        return;
      }
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        // do not allow text entry for now:
        if (e.key === "Backspace" || e.key.length === 1) e.preventDefault();
        return;
        // let source = selection.source;
        // if (e.key === "Backspace") {
        //   onUpdateText(
        //     [source],
        //     [
        //       TextSelection.replace(
        //         source.sourceGabc,
        //         "",
        //         activeTags,
        //         start,
        //         end
        //       )
        //     ]
        //   );
        //   e.preventDefault();
        // } else if (e.key.length === 1) {
        //   onUpdateText(
        //     [source],
        //     [
        //       TextSelection.replace(
        //         source.sourceGabc,
        //         e.key,
        //         activeTags,
        //         selection.start,
        //         selection.end
        //       )
        //     ]
        //   );
        //   e.preventDefault();
        // }
      }
    },
    [applyTag, textOptionsByTagName]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [handleKeyDown]);

  return (
    <Panel title="Text" className="border-left">
      <PanelSection title="" className="mb--3">
        <div className="mb-3 d-flex">
          <div className="flex-fill">
          <FontPane
              handleFontChange={handleFontChange}
              selectedValue={doc[fontKey] || doc.font || ChantDocument.defaultFont}
            />
          </div>
          {selectedTextType.baseFontSize && (
            <div className="ml-3">
              <Panel.Label>Size</Panel.Label>
              <NumericFormControl
                defaultValue={selectedTextType.calculatedFontSize}
                onChange={calculated =>
                  selectedTextType.handleTextSizeChange(
                    calculated,
                    props.onChange
                  )
                }
                className="text-right"
                required
                min={selectedTextType.min}
                max={selectedTextType.max}
                step="0.1"
                style={{
                  width: `${selectedTextType.max.toFixed(1).length + 2.5}ch`
                }}
              />
            </div>
          )}
        </div>
        <div className="mb-3">
          <Panel.Label>Color</Panel.Label>
          <ColorPicker
            colors={colors}
            color={doc[colorKey] || defaultColor}
            onChange={color => handleColorChange(color.hex)}
          />
        </div>
        <div className="mb-3">
          <Panel.Label>Text Options</Panel.Label>
          {textOptions.map(({ tagName, icon, iconClass }) => (
            <Button
              key={tagName}
              variant="square"
              className={iconClass}
              // className="btn-svg"
              active={activeTags[tagName]}
              value={tagName}
              onClick={() => applyTag(tagName)}
            >
              {icon}
            </Button>
          ))}
        </div>
        {!!TextMarkButtons.length && (
          <div className="mb-3">
            <Panel.Label>Text Marks</Panel.Label>
            {TextMarkButtons}
          </div>
        )}
        {isTitleSelected && (
          <div className="mb-3">
            <Panel.Label>Alignment</Panel.Label>
            {[
              { align: "left", faIcon: faAlignLeft },
              { align: "center", faIcon: faAlignCenter },
              { align: "right", faIcon: faAlignRight }
            ].map(({ align, faIcon }) => (
              <Button
                key={align}
                variant="square"
                active={activeAlignment === align}
                value={align}
                onClick={() => handleAlignmentChange(align)}
              >
                <FontAwesomeIcon icon={faIcon} />
              </Button>
            ))}
          </div>
        )}
        {isSyllabified !== null && (
          <div className="mb-3">
            <Panel.Label>Syllables</Panel.Label>
            {isSyllabified ? (
              <Button style={{ width: "100%" }} onClick={unsyllabifySelection}>
                Unsyllabify
              </Button>
            ) : (
              <Button style={{ width: "100%" }} onClick={syllabifySelection}>
                Syl-lab-i-fy
              </Button>
            )}
          </div>
        )}
      </PanelSection>
    </Panel>
  );
}

export default TextPanel;
